import React from "react";
import VariantPanel, { type VariantFact, type VariantItem } from "@/components/common/VariantPanel";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface GroupCopy {
  eyebrow: string;
  title: string;
  lead: string;
  points: string[];
  facts: VariantFact[];
  imageAlt: string;
  itemsHeading: string;
  pipesTitle: string;
  pipesText: string;
  pipesFacts: VariantFact[];
  pipesImageAlt: string;
  tubesTitle: string;
  tubesText: string;
  tubesFacts: VariantFact[];
  tubesImageAlt: string;
}
const copyData: Record<Lang, GroupCopy> = {
  en: {
    eyebrow: "Cylindrical Screen & Tube Assemblies",
    title: "Precision Screen Tubes",
    lead: "Engineered stainless steel mesh and wedge-profile cylinders built for demanding filtration, aggressive fluids, and high collapse resistance across uniform pore geometries.",
    points: ["Rigid welded longitudinal and helical seam construction delivers burr-free surfaces and high structural integrity.", "Custom end rings, flanges, threaded fittings, and internal perforated cores tailored for severe differential pressure.", "Reusable and cleanable via ultrasonic baths, chemical flushing, and backwashing cycles."],
    facts: [{
      label: "Slot / Pore Rating",
      value: "0.02 – 5.0 mm"
    }, {
      label: "Cylinder OD",
      value: "10 – 600 mm"
    }, {
      label: "Alloy Grades",
      value: "AISI 304 / 316L"
    }, {
      label: "Element Length",
      value: "Up to 3,000 mm"
    }],
    imageAlt: "Stainless steel wire mesh screen tube cylinder",
    itemsHeading: "Specialized Screen Tube Configurations",
    pipesTitle: "Screen Pipes & Lateral Hubs",
    pipesText: "Continuous V-profile wire welded to longitudinal support rods for deep intake wells, media underdrains, and uniform fluid distribution.",
    pipesFacts: [{
      label: "Slot Range",
      value: "0.02 – 5.0 mm"
    }, {
      label: "Slot Tolerance",
      value: "±0.015 mm"
    }, {
      label: "Materials",
      value: "304L / 316L"
    }, {
      label: "Flow Direction",
      value: "FOTI / FITO"
    }],
    pipesImageAlt: "Stainless steel wedge wire screen pipe with continuous slot and support rods",
    tubesTitle: "Wedge Wire Tubes & Candle Elements",
    tubesText: "All-welded continuous-slot screen tubes and tubular candle elements engineered for high-pressure separation, catalyst retention, and severe solid-liquid straining.",
    tubesFacts: [{
      label: "Slot Openings",
      value: "0.02 – 5.0 mm"
    }, {
      label: "Materials",
      value: "304L / 316L"
    }, {
      label: "Standard Lengths",
      value: "500 – 3,000 mm"
    }, {
      label: "End Connections",
      value: "Thread / Flange / Weld"
    }],
    tubesImageAlt: "Industrial stainless steel wedge wire candle filter element for pressurized vessels"
  },
  zh: {
    eyebrow: "圆柱形筛管与滤筒组件",
    title: "精密筛管与滤筒",
    lead: "采用定制加工不锈钢丝网与楔形截面圆筒结构，适用于强腐蚀性流体与高压工况过滤，具备均一的孔隙几何结构与优异的抗压溃强度。",
    points: ["自动化纵向与螺旋焊接结构，提供平整无毛刺表面与高强度抗形变能力。", "提供定制端环、法兰、螺纹接头及内部冲孔骨架，耐受严苛工作压差。", "支持超声波清洗、化学浸洗及反冲洗循环，实现长期可靠重复使用。"],
    facts: [{
      label: "缝隙 / 过滤精度",
      value: "0.02 – 5.0 mm"
    }, {
      label: "圆筒外径",
      value: "10 – 600 mm"
    }, {
      label: "标准材质",
      value: "AISI 304 / 316L"
    }, {
      label: "单支长度",
      value: "最高 3,000 mm"
    }],
    imageAlt: "不锈钢编织及烧结丝网精密滤筒筛管",
    itemsHeading: "专用筛管与管状构件系列",
    pipesTitle: "滤水筛管与集配水母支管",
    pipesText: "采用连续 V 型截面丝全焊于纵向支撑筋条结构，为深水取水井、树脂截留及压力容器提供可靠介质截留与均匀流体分布。",
    pipesFacts: [{
      label: "缝隙范围",
      value: "0.02 – 5.0 mm"
    }, {
      label: "缝隙公差",
      value: "±0.015 mm"
    }, {
      label: "材质牌号",
      value: "304L / 316L"
    }, {
      label: "流向形式",
      value: "外进内出 / 内进外出"
    }],
    pipesImageAlt: "不锈钢锲形丝连续缝隙滤水筛管与支撑筋条结构",
    tubesTitle: "楔形丝烛式滤芯与筛管",
    tubesText: "全焊式连续精密缝隙楔形丝滤芯与筛管，专为高压固液分离、催化剂回收及严苛工艺介质截留而设计。",
    tubesFacts: [{
      label: "缝隙精度",
      value: "0.02 – 5.0 mm"
    }, {
      label: "主要材质",
      value: "304L / 316L"
    }, {
      label: "标准长度",
      value: "500 – 3,000 mm"
    }, {
      label: "端部连接",
      value: "螺纹 / 法兰 / 焊接"
    }],
    tubesImageAlt: "用于承压容器的不锈钢全焊式楔形丝烛式滤芯与过滤管"
  },
  es: {
    eyebrow: "Conjuntos de tubos y cribas cilíndricas",
    title: "Tubos de filtración de precisión",
    lead: "Cilindros de malla de alambre y perfil de cuña de acero inoxidable diseñados para filtración exigente, fluidos agresivos y alta resistencia al colapso con geometría uniforme de poros.",
    points: ["Construcción soldada longitudinal y helicoidal sin rebabas con alta integridad estructural.", "Anillos terminales, bridas, racores roscados y núcleos perforados internos para presiones diferenciales severas.", "Reutilizables y lavables mediante ultrasonidos, lavados químicos y ciclos de retrolavado."],
    facts: [{
      label: "Rango de ranura",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Diámetro exterior",
      value: "10 – 600 mm"
    }, {
      label: "Aleaciones",
      value: "AISI 304 / 316L"
    }, {
      label: "Longitud",
      value: "Hasta 3.000 mm"
    }],
    imageAlt: "Cilindro de tubo filtrante de malla de alambre de acero inoxidable",
    itemsHeading: "Configuraciones tubulares especializadas",
    pipesTitle: "Tuberías ranuradas y colectores laterales",
    pipesText: "Alambre continuo de perfil en V soldado por fusión a varillas de soporte longitudinales para pozos de captación, drenajes inferiores y distribución uniforme de fluidos.",
    pipesFacts: [{
      label: "Rango de ranura",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Tolerancia",
      value: "±0,015 mm"
    }, {
      label: "Materiales",
      value: "304L / 316L"
    }, {
      label: "Flujo",
      value: "FOTI / FITO"
    }],
    pipesImageAlt: "Tubo de criba de alambre de cuña de acero inoxidable con ranura continua y varillas de soporte",
    tubesTitle: "Tubos de alambre cuña y elementos tipo bujía",
    tubesText: "Tubos de ranura continua y elementos tipo bujía totalmente soldados para separación a alta presión, recuperación de catalizadores y cribado sólido-líquido severo.",
    tubesFacts: [{
      label: "Aberturas",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Aleaciones",
      value: "304L / 316L"
    }, {
      label: "Longitudes",
      value: "500 – 3.000 mm"
    }, {
      label: "Conexiones",
      value: "Rosca / Brida / Soldadura"
    }],
    tubesImageAlt: "Elemento filtrante de tubo de alambre cuña de acero inoxidable para recipientes a presión"
  },
  de: {
    eyebrow: "Zylindrische Filter- & Siebrohr-Baugruppen",
    title: "Präzisions-Filterrohre",
    lead: "Präzisionsgefertigte Maschenzylinder und Spaltrohre aus Edelstahl für anspruchsvolle Filtrationsaufgaben, aggressive Medien und hohe Kollapsfestigkeit bei gleichmäßiger Porengeometrie.",
    points: ["Stabile Längs- und Spiralschweißkonstruktionen garantieren gratfreie Oberflächen und dauerhafte Formstabilität.", "Individuelle Endringe, Flansche, Gewindeanschlüsse und gelochte Innenkerne für extreme Differenzdrücke.", "Vollständig regenerierbar und reinigbar durch Ultraschallbad, chemische Spülung und Rückspülzyklen."],
    facts: [{
      label: "Spaltweitenbereich",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Außendurchmesser",
      value: "10 – 600 mm"
    }, {
      label: "Werkstoffe",
      value: "AISI 304 / 316L"
    }, {
      label: "Gesamtlänge",
      value: "Bis zu 3.000 mm"
    }],
    imageAlt: "Edelstahl-Drahtgewebe-Filterrohrzylinder",
    itemsHeading: "Spezifische Siebrohr-Konfigurationen",
    pipesTitle: "Filterrohre & Sternverteilersysteme",
    pipesText: "Kontinuierlicher V-Profildraht, widerstandsverschweißt auf Längstragstäben für Tiefbrunnen, Harzfallen und gleichmäßige Medienverteilung.",
    pipesFacts: [{
      label: "Spaltweite",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Toleranz",
      value: "±0,015 mm"
    }, {
      label: "Werkstoffe",
      value: "304L / 316L"
    }, {
      label: "Strömungsrichtung",
      value: "FOTI / FITO"
    }],
    pipesImageAlt: "Spaltrohr aus Edelstahl mit kontinuierlicher Spaltweite und Längstragstäben",
    tubesTitle: "Spaltrohre & Spaltfilterelemente",
    tubesText: "Vollverschweißte Endlosspalt-Spaltrohre und Filterkerzen für Hochdrucktrennung, Katalysatorrückgewinnung und anspruchsvolle Fest-Flüssig-Filtration.",
    tubesFacts: [{
      label: "Spaltöffnungen",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Werkstoffe",
      value: "304L / 316L"
    }, {
      label: "Standardlängen",
      value: "500 – 3.000 mm"
    }, {
      label: "Anschlüsse",
      value: "Gewinde / Flansch / Schweißende"
    }],
    tubesImageAlt: "Industrielles Spaltrohr-Filterelement aus Edelstahl für Druckbehälter"
  },
  fr: {
    eyebrow: "Ensembles de tubes et crépines cylindriques",
    title: "Tubes filtrants de précision",
    lead: "Cylindres en toile métallique et fil en coin d'acier inoxydable conçus pour filtrations exigeantes, fluides agressifs et haute résistance à l'écrasement avec porosité uniforme.",
    points: ["Construction mécano-soudée longitudinale et hélicoïdale assurant des surfaces sans bavures et une parfaite rigidité.", "Bagues d'extrémité, brides, raccords filetés et noyaux perforés pour résister aux fortes pressions différentielles.", "Nettoyables et réutilisables par ultrasons, rinçage chimique et rétro-lavage."],
    facts: [{
      label: "Ouverture de fente",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Diamètre extérieur",
      value: "10 – 600 mm"
    }, {
      label: "Nuances d'alliage",
      value: "AISI 304 / 316L"
    }, {
      label: "Longueur max.",
      value: "Jusqu'à 3 000 mm"
    }],
    imageAlt: "Tube filtrant cylindrique en toile métallique d'acier inoxydable",
    itemsHeading: "Configurations tubulaires spécialisées",
    pipesTitle: "Tubes crépines et collecteurs latéraux",
    pipesText: "Fil continu à profil en V soudé par fusion sur des tiges longitudinales pour puits d'eau, rétention de résine et distribution uniforme des fluides.",
    pipesFacts: [{
      label: "Plage d'ouverture",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Tolérance",
      value: "±0,015 mm"
    }, {
      label: "Matériaux",
      value: "304L / 316L"
    }, {
      label: "Sens de flux",
      value: "FOTI / FITO"
    }],
    pipesImageAlt: "Tube crépine en acier inoxydable à fente continue et tiges de support",
    tubesTitle: "Tubes à fil en coin et bougies filtrantes",
    tubesText: "Tubes à fente continue et éléments bougies soudés conçus pour la séparation haute pression, la récupération de catalyseur et la filtration solide-liquide sévère.",
    tubesFacts: [{
      label: "Ouvertures de fente",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Alliages",
      value: "304L / 316L"
    }, {
      label: "Longueurs standards",
      value: "500 – 3 000 mm"
    }, {
      label: "Raccordements",
      value: "Filetage / Bride / Soudure"
    }],
    tubesImageAlt: "Élément filtrant en fil en coin en acier inoxydable pour cuves sous pression"
  },
  pt: {
    eyebrow: "Conjuntos de tubos e peneiras cilíndricas",
    title: "Tubos de filtragem de precisão",
    lead: "Cilindros de tela metálica e arame em cunha sob medida para condições exigentes, fluidos agressivos e alta resistência ao colapso com geometria uniforme de poros.",
    points: ["Construção soldada longitudinal e helicoidal sem rebarbas com alta integridade mecânica.", "Anéis terminais, flanges, conexões roscadas e núcleos perfurados sob medida para alta pressão diferencial.", "Reutilizáveis e regeneráveis via banho ultrassônico, lavagem química e ciclos de retrolavagem."],
    facts: [{
      label: "Abertura de ranhura",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Diâmetro externo",
      value: "10 – 600 mm"
    }, {
      label: "Ligas de aço",
      value: "AISI 304 / 316L"
    }, {
      label: "Comprimento",
      value: "Até 3.000 mm"
    }],
    imageAlt: "Cilindro de tubo filtrante de malha de aço inoxidável",
    itemsHeading: "Configurações tubulares especializadas",
    pipesTitle: "Tubos de peneira e coletores laterais",
    pipesText: "Arame contínuo de perfil em V soldado por fusão a hastes longitudinais para poços profundos, retenção de leito e distribuição uniforme de fluidos.",
    pipesFacts: [{
      label: "Faixa de ranhura",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Tolerância",
      value: "±0,015 mm"
    }, {
      label: "Materiais",
      value: "304L / 316L"
    }, {
      label: "Fluxo",
      value: "FOTI / FITO"
    }],
    pipesImageAlt: "Tubo de peneira de arame em cunha de aço inoxidável com ranhura contínua e hastes de suporte",
    tubesTitle: "Tubos tipo cunha e elementos vela",
    tubesText: "Tubos de tela perfilada e elementos filtrantes tipo vela totalmente soldados para separação sob alta pressão, recuperação de catalisadores e filtração rigorosa.",
    tubesFacts: [{
      label: "Abertura de ranhura",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Ligas",
      value: "304L / 316L"
    }, {
      label: "Comprimentos",
      value: "500 – 3.000 mm"
    }, {
      label: "Terminações",
      value: "Rosca / Flange / Solda"
    }],
    tubesImageAlt: "Elemento filtrante de tubo de tela tipo cunha em aço inoxidável para vasos pressurizados"
  }
};
export default function ScreenTubesGroup() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  const items: VariantItem[] = [{
    id: "screen-pipes",
    title: copy.pipesTitle,
    text: copy.pipesText,
    facts: copy.pipesFacts,
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
      alt: copy.pipesImageAlt
    },
    href: "/ScreenPipesPage"
  }, {
    id: "wedge-wire-tubes",
    title: copy.tubesTitle,
    text: copy.tubesText,
    facts: copy.tubesFacts,
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
      alt: copy.tubesImageAlt
    },
    href: "/WedgeWireTubesPage"
  }];
  return <VariantPanel id="screen-tubes" eyebrow={copy.eyebrow} title={copy.title} lead={copy.lead} points={copy.points} facts={copy.facts} image={{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
    alt: copy.imageAlt
  }} tone="muted" itemsHeading={copy.itemsHeading} items={items} href="/ScreenTubesPage" />;
}